import type { Attachment, LinkPreview } from '@novachat/shared';
import { Download, FileArchive, FileText, File as FileIcon, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { client } from '../lib/client';
import { fileUrl, sizeText } from '../lib/upload';

/** A signed link that renews itself once if it has expired (links last about a day). */
function useFileLink(attachment: Attachment, variant: 'original' | 'preview') {
  const pick = (a: Attachment) => fileUrl(variant === 'preview' ? (a.previewUrl ?? a.url) : a.url);
  const [src, setSrc] = useState(() => pick(attachment));
  const renewed = useRef(false);
  const renew = () => {
    if (renewed.current) return;
    renewed.current = true;
    client.api
      .attachmentLink(attachment.id)
      .then((fresh) => setSrc(pick(fresh)))
      .catch(() => {});
  };
  return [src, renew] as const;
}

const downloadUrl = (url: string) => `${url}${url.includes('?') ? '&' : '?'}dl=1`;

function ImageViewer({ attachment, onClose }: { attachment: Attachment; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [src, renew] = useFileLink(attachment, 'original');
  useEffect(() => dialog.current?.showModal(), []);
  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      onClick={(e) => e.target === dialog.current && dialog.current?.close()}
      aria-label={attachment.fileName}
      className="m-0 h-dvh max-h-none w-screen max-w-none bg-transparent p-0 backdrop:bg-black/85"
    >
      <div className="pointer-events-none flex h-full flex-col">
        <div className="pointer-events-auto flex items-center gap-3 px-4 pt-[max(12px,env(safe-area-inset-top))] pb-3 text-sm text-white">
          <span className="min-w-0 flex-1 truncate font-semibold">{attachment.fileName}</span>
          <span className="text-white/60 max-sm:hidden">{sizeText(attachment.size)}</span>
          <a href={downloadUrl(src)} className="flex h-9 items-center gap-1.5 rounded-sm bg-white/10 px-3 font-semibold text-white hover:bg-white/20">
            <Download size={16} aria-hidden="true" />
            İndir
          </a>
          <button type="button" aria-label="Kapat" onClick={() => dialog.current?.close()} className="grid size-9 place-items-center rounded-sm border-0 bg-white/10 text-white hover:bg-white/20">
            <X size={18} />
          </button>
        </div>
        <div className="grid min-h-0 flex-1 place-items-center p-3 pb-[max(12px,env(safe-area-inset-bottom))]">
          <img src={src} onError={renew} alt={attachment.fileName} className="pointer-events-auto max-h-full max-w-full object-contain" />
        </div>
      </div>
    </dialog>
  );
}

function ImageTile({ attachment, single }: { attachment: Attachment; single: boolean }) {
  const [src, renew] = useFileLink(attachment, 'preview');
  const [open, setOpen] = useState(false);
  const width = attachment.width ?? 4;
  const height = attachment.height ?? 3;
  // One image keeps its shape (at most 400×320); several share a tidy square grid.
  const shown = single ? Math.min(400, width, (320 * width) / height) : undefined;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`${attachment.fileName} görselini büyüt`}
        className="block overflow-hidden rounded-md border border-line bg-surface-2 p-0"
        style={single ? { width: shown, maxWidth: '100%', aspectRatio: `${width} / ${height}` } : { aspectRatio: '1' }}
      >
        <img src={src} onError={renew} alt={attachment.fileName} loading="lazy" decoding="async" className="size-full object-cover" />
      </button>
      {open && <ImageViewer attachment={attachment} onClose={() => setOpen(false)} />}
    </>
  );
}

function Media({ attachment }: { attachment: Attachment }) {
  const [src, renew] = useFileLink(attachment, 'original');
  if (attachment.kind === 'audio') return <audio controls preload="none" src={src} onError={renew} className="w-full max-w-[400px]" />;
  return <video controls preload="metadata" playsInline src={src} onError={renew} className="max-h-[360px] w-full max-w-[480px] rounded-md border border-line bg-black" />;
}

function FileCard({ attachment }: { attachment: Attachment }) {
  const [src] = useFileLink(attachment, 'original');
  const Icon = /zip|rar|7z|tar|gzip/.test(attachment.mime) ? FileArchive : /pdf|text|word|document|sheet|presentation/.test(attachment.mime) ? FileText : FileIcon;
  return (
    <a
      href={downloadUrl(src)}
      className="flex max-w-[380px] items-center gap-3 rounded-md border border-line bg-surface-2 px-3 py-2.5 text-fg-1 hover:border-line-strong"
    >
      <span className="grid size-10 flex-none place-items-center rounded-sm bg-indigo-400/20 text-indigo-200">
        <Icon size={20} aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold">{attachment.fileName}</span>
        <span className="block text-xs text-fg-3">{sizeText(attachment.size)}</span>
      </span>
      <Download size={18} className="flex-none text-fg-2" aria-label="İndir" />
    </a>
  );
}

/** A card for a linked page; its picture comes from our server, never from the site itself. */
export function LinkCards({ links }: { links: readonly LinkPreview[] }) {
  if (links.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      {links.map((link) => (
        <a
          key={link.url}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex max-w-[460px] gap-3 rounded-md border border-l-[3px] border-line border-l-teal-400 bg-surface-2 p-3 hover:border-line-strong"
        >
          <span className="min-w-0 flex-1">
            {link.siteName && <span className="block truncate text-xs text-fg-3">{link.siteName}</span>}
            <span className="block text-sm font-semibold text-teal-400 [overflow-wrap:anywhere]">{link.title}</span>
            {link.description && <span className="mt-0.5 line-clamp-2 block text-[13px] text-fg-2">{link.description}</span>}
          </span>
          {link.imageUrl && <img src={fileUrl(link.imageUrl)} alt="" loading="lazy" className="size-16 flex-none rounded-sm object-cover" />}
        </a>
      ))}
    </div>
  );
}

/** Files under a message: images first (one large or a grid), then players and download cards. */
export function Attachments({ items }: { items: readonly Attachment[] }) {
  if (items.length === 0) return null;
  const images = items.filter((a) => a.kind === 'image');
  const others = items.filter((a) => a.kind !== 'image');
  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      {images.length > 0 && (
        <div className={images.length === 1 ? '' : 'grid max-w-[420px] grid-cols-2 gap-1.5'}>
          {images.map((a) => (
            <ImageTile key={a.id} attachment={a} single={images.length === 1} />
          ))}
        </div>
      )}
      {others.map((a) => (a.kind === 'file' ? <FileCard key={a.id} attachment={a} /> : <Media key={a.id} attachment={a} />))}
    </div>
  );
}
